import { Component, OnDestroy, OnInit } from '@angular/core';
import { NotificationService } from '../../services/notification.service';
import { catchError, EMPTY, Subject, take, tap } from 'rxjs';
import { UserNotification } from '../models/user-notification.model';
import { MessageService } from '../../services/message.service';
import { Router } from '@angular/router';
import { RoleGuardService } from '../../role-guard.service';
import { AuthService } from '../../core/auth-service';

@Component({
  selector: 'app-notification-bell',
  templateUrl: './notification-bell.component.html',
  styleUrl: './notification-bell.component.scss'
})
export class NotificationBellComponent implements OnInit, OnDestroy {

  loading: boolean;
  hasNewAlert: boolean;
  destroy$ = new Subject();
  touchStartX: number;
  swipeId: number;
  notifications: Array<UserNotification>;
  inTheGame: boolean;

  constructor(private notificationService: NotificationService,
    private messageService: MessageService,
    private router: Router,
    private roleGuardService: RoleGuardService
  ) { }
  
  ngOnInit(): void {
    this.loading = true;
    this.hasNewAlert = false;
    this.inTheGame = this.roleGuardService.hasRole(AuthService.viewTrophies);
    this.messageService.subscribeToNotifications((notifications: Array<UserNotification>) => {
      this.notifications = notifications.filter(x => this.isActive(x));
      if (this.notifications.find(x => !x.isRead)) {
        this.hasNewAlert = true;
      }
      this.loading = false;
    }, (notification: UserNotification) => {
      if (this.isActive(notification)) {
        if (notification.isRead) {
          this.remove(notification);
          if (!this.notifications.find(x => !x.isRead)) {
            this.hasNewAlert = false;
          }
        } else {
          // New notification needs to be at the start
          console.log(`Added new notification ${notification.description}`);
          this.notifications = [notification].concat(this.notifications);
          this.hasNewAlert = true;
        }
      }
    });
  }
  
  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

  private isActive(notification: UserNotification): boolean {
    // Ignore pog notifications if they don't have the associated role
    if (notification.notificationType === 2 && !this.inTheGame) {
      return false;
    }
    return true;
  }

  remove(notification: UserNotification) {
    const index = this.notifications.findIndex(x => x.notificationId === notification.notificationId);
    if (index !== -1) {
        this.notifications.splice(index, 1);
    }
  }

  popup() {
    this.hasNewAlert = false;
    this.notifications.forEach(notification => {
      if (notification.isSeen) {
        notification.isRead = true;
      }
      notification.isSeen = true;
    });
  }

  get noNotifications(): boolean {
    if (this.notifications) {
      return this.notifications.length === 0;
    }
    return true;
  }

  viewAllNotifications() {
    this.router.navigate(['/notifications']);
    this.messageService.closeNotificationPopupByEvent();
  }

  read(notification: UserNotification, view: boolean) {
    this.messageService.readNotification$(notification.notificationId).pipe(
      tap(() => {
        if (view) {
          this.messageService.navigateToNotification(notification);
        }
      }),
      catchError(err => {
        this.notificationService.dangerToast(
          'Failed to read notification',
          err
        );
        return EMPTY;
      }),
      take(1)
    ).subscribe();
    this.remove(notification);
  }

  swipeStart(notification: UserNotification, event: TouchEvent) {
    this.touchStartX = event.changedTouches[0].screenX;
    this.swipeId = notification.notificationId;
  }

  swipeEnd(notification: UserNotification, event: TouchEvent) {
    const touchEndX = event.changedTouches[0].screenX;
    // Don't care if they swipe left or right
    if (notification.notificationId === this.swipeId && (touchEndX > this.touchStartX || touchEndX < this.touchStartX)) {
      this.read(notification, false);
    }
    this.touchStartX = null;
    this.swipeId = null;
  }
}
